import isEmail from 'validator/lib/isEmail';
import { REGIONS_BY_COUNTRY } from './constants';

const getStateValidationPattern = (country = 'United States of America') =>
  new RegExp(`^(${(REGIONS_BY_COUNTRY[country] || []).join('|')})$`, 'i');

export const getFormFieldValidations = (country: string | null, useEcmCompanyLength?: boolean) => {
  const companyLength = useEcmCompanyLength
    ? /^[a-z0-9 ,.&'\-:+/\\_[\]|~]{2,100}$/i
    : /^[a-z0-9 ,.&'\-:+/\\_[\]|~]{2,50}$/i;
  return {
    companyName: companyLength,
    firstName: /^[a-z ,.'-]*$/i,
    lastName: /^[a-z ,.'-]*$/i,
    street1: /^[a-z0-9 ,.'#_[|~/\-\\\]]{2,}$/i,
    street2: /^[a-z0-9 ,.'#_[|~/\-\\\]]*$/i,
    city: /^[a-z0-9 ,.'-]{2,}$/i,
    state: getStateValidationPattern(country == null ? 'United States of America' : country),
    phone: /^(?=.{1,30}$)([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([\s]?[-]?[\s]?[0-9x])*$/,
    postalCode:
      country === 'CA' || country === 'Canada' ? /[A-Za-z]\d[A-Za-z]\s?-?\d[A-Za-z]\d/ : /^\d{5}(?:[-\s]\d{4})?$/,
    password: /^.{1,50}$/i,
  };
};

export const isEmailValid = (email?: string) => !!email && isEmail(email) && email.length <= 50;

const { companyName: companyNameValidationRegex } = getFormFieldValidations('');

const streetValidCharactersMessage =
  'Addresses can include letters, numbers, spaces, and the following symbols: ' + ", . ' - # _ [ | ~ / \\ ]";

export const getFieldErrorMessage = (field: string, value: string | null, useEcmCompanyLength?: boolean) => {
  // Check empty values
  if (value === '' || value == null) return 'This field is required';

  switch (field) {
    case 'companyName':
      if (value.length < 2) return 'Company name is invalid';
      if (useEcmCompanyLength && value.length > 100) return 'Company name must be fewer than 100 characters';
      if (!useEcmCompanyLength && value.length > 50) return 'Company name must be fewer than 50 characters';
      if (!value.match(companyNameValidationRegex)) {
        return (
          'Company names can include letters, numbers, spaces, and the following symbols: ' +
          ", . & ' - : + / \\ _ [ ] | ~"
        );
      }

      return 'Company name is invalid';
    case 'city':
    case 'street1':
      if (value.length < 2) {
        return 'Address is invalid';
      }
      if (value.length > 50) {
        return 'Address must be fewer than 50 characters';
      }
      return streetValidCharactersMessage;
    case 'email':
      if (value.match(/(\.\.)/)) {
        return 'Email should not include consecutive periods';
      }
      if (!value.match('@')) {
        return 'Email should include an @ sign';
      }
      if (value.split('@').length < 3 && !value.split('@')[1].match(/\./)) {
        return 'Email should include a dot';
      }
      if (value.split('@').length < 3 && value.split('@')[1].split('.')[1].length < 2) {
        return 'Please complete your email address';
      }
      if (value.length > 50) {
        return 'Email address is too long. Try an email shorter than 50 characters.';
      }
      return 'Email is invalid';

    case 'phone':
      if (value.match(/\D/g)) {
        return 'Just numbers, no dashes.';
      }
      return 'Phone number is invalid';
    case 'street2':
      return streetValidCharactersMessage;
    case 'password':
      return 'Try a more complex password';
    default:
      return 'Input is invalid';
  }
};
